import {
  getCurrentFocusKey,
  setFocus,
} from "@noriginmedia/norigin-spatial-navigation"
import { ClientError, GraphQLClient } from "graphql-request"
import { GraphQLError } from "graphql/error"
import i18n from "i18next"
import { useMemo, useRef } from "react"
import { useLocation } from "react-router"

import { Pages } from "../App"
import { getSdk, SdkFunctionWrapper } from "../generated/graphql"
import { appStore, useAuth } from "../store/AppStore"
import { CuratedContentSlugV2, exitApp } from "../utilities/common"
import { env } from "../utilities/constants"
import { addBreadCrumb, captureSentryException } from "../utilities/sentry"

import { useSmoothNavigate } from "./use-smooth-navigate"
import useTranslate from "./use-translate"
export * from "../generated/graphql"

const extractVariablesObject = (str: string) => {
  const key = '"variables":'
  const startIndex = str.indexOf(key)
  if (startIndex === -1) return null

  // Find the first '{' after "variables":
  const openBraceIndex = str.indexOf("{", startIndex)
  if (openBraceIndex === -1) return null

  // Walk through string to find the matching closing brace
  let depth = 0
  for (let i = openBraceIndex; i < str.length; i++) {
    if (str[i] === "{") depth++
    else if (str[i] === "}") depth--
    if (depth === 0) {
      const jsonStr = str.slice(openBraceIndex, i + 1)
      try {
        return JSON.parse(jsonStr)
      } catch {
        return jsonStr // fallback to raw string if not valid JSON
      }
    }
  }

  return null // no matching closing brace found
}

const API_URL = `${env.backendUri}/graphql`

export const unauthenticatedError = "UNAUTHENTICATED"
export const tokenExpiredError = "TOKEN_EXPIRED"
export type APIErrorCode =
  | typeof unauthenticatedError
  | typeof tokenExpiredError

const client = new GraphQLClient(API_URL)

export function getClientWithHeaders({
  locale = "en",
  token,
}: {
  locale?: string
  token?: string
}): GraphQLClient {
  client.setHeader("Accept-Language", locale)
  //client.setHeader("x-feature-teststream", "https://video-cf.stage-plus.com/v2/DEA282154626/playlist.m3u8")

  if (token) {
    client.setHeader("Authorization", `Bearer ${token}`)
  } else {
    // couldn't find a different way to unset the header
    // also this: https://github.com/Microsoft/TypeScript/issues/19573
    if (
      client &&
      // @ts-expect-error see above
      client.options &&
      // @ts-expect-error see above
      client.options.headers &&
      // @ts-expect-error see above
      client.options.headers.Authorization
    )
      // @ts-expect-error see above
      delete client.options.headers.Authorization
  }

  return client
}

export const GraphQLErrorType = {
  Unauthenticated: "UNAUTHENTICATED",
  TokenExpired: "TOKEN_EXPIRED",
  TokenInvalid: "INVALID_TOKEN",
  NotFound: "NOT_FOUND",
} as const
export function matchesGraphQLErrorType(
  error: GraphQLError & { code?: string },
  errorType: (typeof GraphQLErrorType)[keyof typeof GraphQLErrorType],
): boolean {
  const code =
    "code" in error && typeof error.code === "string"
      ? error.code.toUpperCase()
      : undefined
  const message =
    "message" in error && typeof error.message === "string"
      ? error.message.toUpperCase()
      : undefined
  switch (errorType) {
    case GraphQLErrorType.Unauthenticated:
    case GraphQLErrorType.TokenInvalid:
    case GraphQLErrorType.TokenExpired: {
      return code === errorType
    }
    case GraphQLErrorType.NotFound: {
      return message === errorType
    }
  }
}

type Sdk = ReturnType<typeof getSdk>

export enum ErrorLevel {
  NoError,
  WithPrompt,
}

/*
errorLevel:
0: display error and enable navigate back
1: display error and disable navigate back
2: don't display error
 */
export default function useSdk(
  errorLevel: ErrorLevel = ErrorLevel.NoError,
  dismissCallback?: (values?: unknown) => void,
): Sdk {
  const token = appStore((state) => state.accessTokenState)
  const locale = i18n.language
  const prompt = appStore((state) => state.prompt)
  const setPrompt = appStore((state) => state.setPrompt)
  const setSpinner = appStore((state) => state.setSpinner)
  const location = useLocation()

  const { onTokenExpired, logout } = useAuth()
  const navigate = useSmoothNavigate()
  const t = useTranslate()
  const focusKey = useRef<string | undefined>(null)

  const isAtRoot = location.pathname === "/home"

  // This is memoized so that the SDK instance is not recreated on every render, only when the token or locale changes
  return useMemo(() => {
    // Create a new GraphQL client with the correct locale and token headers
    const client = getClientWithHeaders({ locale, token })

    // A custom error handler that will refresh the token if it has expired or log the user out if the token is invalid
    const wrapper: SdkFunctionWrapper = async (
      action,
      operationName,
      operationType,
    ) => {
      try {
        // Run the request
        return await action()
      } catch (error) {
        let variables
        if (typeof error === "object" && !!error && "message" in error)
          variables = extractVariablesObject(error.message as string)
        addBreadCrumb({
          data: {
            operationName,
            base64EncodedVariables: btoa(JSON.stringify(variables)),
          },
        })
        captureSentryException(error)
        // Check if the error is a ClientError (i.e. a GraphQL error)
        if (error instanceof ClientError && error.response) {
          // rate limit
          if (error.response.status === 429) {
            return await retryWithDelay(
              action,
              operationName,
              operationType,
            ).then((req) => req())
          }
          // Loop through the errors and check if any of them are a known error code
          if (error?.response?.errors)
            for (const clientError of error.response.errors) {
              // Not found errors are thrown when the requested resource is not found
              if (
                matchesGraphQLErrorType(clientError, GraphQLErrorType.NotFound)
              ) {
                if (errorLevel === ErrorLevel.WithPrompt) {
                  return await retry(
                    GraphQLErrorType.NotFound,
                    "ERROR",
                    action,
                    operationName,
                    operationType,
                  )
                    .then((req) => req())
                    .catch(() => {
                      throw new Error(GraphQLErrorType.NotFound)
                    })
                } else throw new Error(GraphQLErrorType.NotFound)
              }
              // Unauthenticated errors are thrown when access is denied due to missing or invalid token
              if (
                matchesGraphQLErrorType(
                  clientError,
                  GraphQLErrorType.Unauthenticated,
                )
              ) {
                // Log the user out and throw the error
                logout()
                throw error
              }
              // Token expired errors are thrown when the token is expired
              if (
                matchesGraphQLErrorType(
                  clientError,
                  GraphQLErrorType.TokenExpired,
                ) ||
                matchesGraphQLErrorType(
                  clientError,
                  GraphQLErrorType.TokenInvalid,
                )
              ) {
                // Refresh the token and retry the request
                await onTokenExpired()
                return action()
              }
              // Otherwise, if the error is not recognized log the error
            }
        }
        if (errorLevel === ErrorLevel.WithPrompt)
          return await retry(
            t("notification_network__no_connection_text"),
            "WIFI",
            action,
            operationName,
            operationType,
          )
            .then((req) => req())
            .catch(() => {
              throw error
            })
        else throw error
      }
    }

    const retryWithDelay = <T>(
      action: (requestHeaders?: Record<string, string>) => Promise<T>,
      operationName: string,
      operationType?: string,
    ): Promise<() => Promise<T>> => {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(() => wrapper<T>(action, operationName, operationType))
        }, 3000)
      })
    }

    const retry = <T>(
      error: string,
      icon: "ERROR" | "WIFI" | "ALERT",
      action: (requestHeaders?: Record<string, string>) => Promise<T>,
      operationName: string,
      operationType?: string,
    ): Promise<() => Promise<T>> => {
      if (!focusKey.current) focusKey.current = getCurrentFocusKey()
      return new Promise((resolve, reject) => {
        if (prompt?.open) {
          reject()
          return
        }
        setPrompt({
          open: true,
          onClose: () => {
            setFocus(focusKey.current!)
            focusKey.current = undefined
            if (dismissCallback) dismissCallback()
            else if (isAtRoot) exitApp()
            else {
              navigate(`/${Pages.Home}`)
              setSpinner("out")
              setFocus("TABBAR:" + CuratedContentSlugV2.Discover)
              document.getElementById("root")!.style.transform =
                "translate3d(0, 0, 0)"
            }
            reject()
          },
          onConfirm: async () => {
            resolve(() => wrapper<T>(action, operationName, operationType))
          },
          confirmButtonText: t("player__error_generic_button"),
          dismissButtonText: isAtRoot ? t("tv__quit_title_smarttv") : undefined,
          dismissible: true,
          title: t("notification__error_generic_title"),
          message: error,
          icon,
        })
      })
    }
    return getSdk(client, wrapper)
  }, [locale, logout, onTokenExpired, token])
}

/**
 * @deprecated Only use this for non-localized API requests from the server
 * Please use `incrementalServerSideWithQuery` instead.
 */
export const sdk = getSdk(client)
export type SDK = typeof sdk
